(() => {
  if (customElements.get('kids-book-personalization')) return;

  class KidsBookPersonalization extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';

      this.inputs = Array.from(this.querySelectorAll('[data-kids-book-input]'));
      this.childInput = this.querySelector('[data-kids-book-role="child"]');
      this.fromInput = this.querySelector('[data-kids-book-role="from"]');
      this.noteInput = this.querySelector('[data-kids-book-role="note"]');
      this.uniqueInput = this.querySelector('[data-kids-book-unique-id]');
      this.optionalSource = this.querySelector('[data-kids-book-optional-source]');
      this.optionalProperty = this.querySelector('[data-kids-book-optional-property]');
      this.form = document.getElementById(this.dataset.formId || '');
      this.storageKey = 'uplift:kids-book-repeat-details:v1';
      this.isRepeatOrder = new URLSearchParams(window.location.search).get('personalize_another') === '1';

      this.restoreRepeatDetails();

      this.inputs.forEach((input) => {
        input.addEventListener('input', () => this.updateInput(input));
        input.addEventListener('blur', () => {
          input.dataset.touched = 'true';
          this.updateInput(input);
        });
        input.addEventListener('invalid', (event) => {
          event.preventDefault();
          input.dataset.touched = 'true';
          this.updateInput(input, true);
          this.focusInvalidInput(input);
        });
        this.updateInput(input);
      });

      this.bindFormValidation();
      this.bindSuccessfulAdd();

      window.addEventListener('pageshow', () => this.clearChildForRepeatOrder());
    }

    readSavedDetails() {
      try {
        return JSON.parse(window.sessionStorage.getItem(this.storageKey) || 'null');
      } catch (error) {
        return null;
      }
    }

    restoreRepeatDetails() {
      if (!this.isRepeatOrder) return;

      const saved = this.readSavedDetails();

      if (saved && this.fromInput && typeof saved.from === 'string') {
        this.fromInput.value = saved.from.slice(0, this.fromInput.maxLength || saved.from.length);
      }

      if (saved && this.noteInput && typeof saved.note === 'string') {
        this.noteInput.value = saved.note.slice(0, this.noteInput.maxLength || saved.note.length);
      }

      this.clearChildForRepeatOrder();
    }

    clearChildForRepeatOrder() {
      if (!this.childInput || !this.isRepeatOrder) return;

      this.childInput.value = '';
      this.childInput.dataset.touched = 'false';
      this.updateInput(this.childInput);
    }

    bindSuccessfulAdd() {
      if (!this.form || this.form.dataset.kidsBookMemoryReady === 'true') return;
      this.form.dataset.kidsBookMemoryReady = 'true';

      this.form.addEventListener('variant:add', () => {
        const details = {
          from: this.fromInput ? this.fromInput.value.trim() : '',
          note: this.noteInput ? this.noteInput.value.trim() : ''
        };

        try {
          window.sessionStorage.setItem(this.storageKey, JSON.stringify(details));
        } catch (error) {
          // Storage can be unavailable in strict privacy modes. The order still works normally.
        }

        if (this.uniqueInput) this.uniqueInput.value = '';
      });
    }

    bindFormValidation() {
      if (!this.form || this.form.dataset.kidsBookValidationReady === 'true') return;
      this.form.dataset.kidsBookValidationReady = 'true';

      this.form.addEventListener('submit', (event) => {
        const invalidInputs = this.inputs.filter((input) => input.required && !input.checkValidity());
        if (!invalidInputs.length) {
          this.ensureUniqueId();
          return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();

        invalidInputs.forEach((input) => {
          input.dataset.touched = 'true';
          this.updateInput(input, true);
        });
        this.focusInvalidInput(invalidInputs[0]);
      }, true);
    }

    ensureUniqueId() {
      if (!this.uniqueInput || this.uniqueInput.value) return;

      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        this.uniqueInput.value = window.crypto.randomUUID();
      } else {
        this.uniqueInput.value = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      }
    }

    focusInvalidInput(input) {
      if (this.invalidFocusFrame) return;

      this.invalidFocusFrame = requestAnimationFrame(() => {
        input.focus({ preventScroll: true });
        input.scrollIntoView({ behavior: 'smooth', block: 'center' });
        this.invalidFocusFrame = null;
      });
    }

    updateInput(input, forceError = false) {
      if (input.maxLength > 0 && input.value.length > input.maxLength) {
        input.value = input.value.slice(0, input.maxLength);
      }

      const value = input.value || '';
      const counterKey = input.dataset.kidsBookCountFor;
      const counter = counterKey ? this.querySelector(`[data-kids-book-count="${counterKey}"]`) : null;
      const field = input.closest('[data-kids-book-field]');
      const error = field?.querySelector('[data-kids-book-error]');
      const hasValue = value.trim().length > 0;
      const isInvalid = input.required && !hasValue;
      const showError = isInvalid && (forceError || input.dataset.touched === 'true');

      if (counter) counter.textContent = String(value.length);
      if (field) {
        field.classList.toggle('is-valid', input.required && hasValue && !isInvalid);
        field.classList.toggle('is-invalid', showError);
      }

      input.setAttribute('aria-invalid', showError ? 'true' : 'false');
      if (error) error.hidden = !showError;

      if (input === this.optionalSource && this.optionalProperty) {
        this.optionalProperty.value = value.trim();
        this.optionalProperty.disabled = !hasValue;
      }
    }
  }

  customElements.define('kids-book-personalization', KidsBookPersonalization);
})();
